import {
  DEFAULT_CHARACTER_SCALE_MULTIPLIER,
  DEFAULT_SCENE_CHARACTER_SCALE,
  normalizeCharacterScaleMultiplier,
  normalizeCharacterScale,
  type CharacterScale,
} from '../../game/avatarScale';
import { normalizeSceneStatus } from '../../game/sceneLoader';
import { normalizeWorldLayout, type WorldLayout } from '../../game/worldLayout';
import {
  cloneMovementConfig,
  parseMovementConfigCandidate,
  toLegacyWaypointPath,
} from '../../lib/npcMovement';
import { canonicalizeProviderModel, cloneFallbackProviders } from '../../lib/llmConfig';
import { normalizeAvatarLayerSelection } from '../../lib/avatarLayerSelection';
import { normalizeInlineSpriteColors } from '../../lib/spriteColors';
import { normalizeWorldAnnotations } from '../../lib/worldAnnotations';
import {
  normalizeAnnotationRuntimeStateById,
  stripRuntimeStateFromAnnotations,
} from '../../lib/worldAnnotationRuntime';
import { normalizeAnnotationTriggerRuntimeStateById as normalizeTriggerRuntimeStateById } from '../../lib/worldAnnotationTriggers';
import type { SceneDocument, SceneNpcPlacement } from '../../types/scene';
import { sanitizeAnimatedGifPlacements } from './sceneAnimatedGifHelpers';
import { normalizeWritableStatus, sanitizeSceneName, sanitizeTags } from './sceneWriteHelpers';
import type { SceneCompositionInput } from './scenes';

const toNumber = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const toTimestampDate = (value: unknown): Date | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as { toDate?: () => Date };
  if (typeof candidate.toDate !== 'function') {
    return null;
  }

  try {
    return candidate.toDate();
  } catch {
    return null;
  }
};

export const sortScenesByRecency = (scenes: SceneDocument[]): SceneDocument[] =>
  [...scenes].sort((left, right) => {
    const leftTime =
      left.publishedAt?.getTime() ?? left.updatedAt?.getTime() ?? left.createdAt?.getTime() ?? 0;
    const rightTime =
      right.publishedAt?.getTime() ?? right.updatedAt?.getTime() ?? right.createdAt?.getTime() ?? 0;
    return rightTime - leftTime;
  });

const sanitizePlacementId = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9:_-]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'placement';

const sanitizeTileCoordinate = (value: number, fallback: number, maximum: number): number => {
  const parsed = value;
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.max(0, Math.min(maximum, Math.floor(parsed)));
};

const sanitizePlacementWaypoints = (
  value: SceneNpcPlacement['waypointPath'],
  layout: WorldLayout,
): Array<{ x: number; y: number }> =>
  Array.isArray(value)
    ? value
        .map((waypoint) => ({
          x: Math.max(0, Math.min(layout.tileWidth - 1, Math.floor(waypoint.x))),
          y: Math.max(0, Math.min(layout.tileHeight - 1, Math.floor(waypoint.y))),
        }))
        .slice(0, 40)
    : [];

const sanitizePlacementMovementConfig = (
  value: SceneNpcPlacement['movementConfig'],
  waypoints: Array<{ x: number; y: number }>,
  layout: WorldLayout,
): NonNullable<SceneNpcPlacement['movementConfig']> =>
  cloneMovementConfig(parseMovementConfigCandidate(value, waypoints, layout), layout);

export const sanitizeSceneNpcPlacement = (
  value: SceneNpcPlacement,
  fallbackPlacementId: string,
  layout: WorldLayout,
): SceneNpcPlacement => {
  const waypointPath = sanitizePlacementWaypoints(value.waypointPath, layout);
  const movementConfig = sanitizePlacementMovementConfig(
    value.movementConfig,
    waypointPath,
    layout,
  );
  const spawnX = sanitizeTileCoordinate(value.spawnX, 2, layout.tileWidth - 1);
  const spawnY = sanitizeTileCoordinate(value.spawnY, 2, layout.tileHeight - 1);

  const normalized: SceneNpcPlacement = {
    placementId: sanitizePlacementId(value.placementId || fallbackPlacementId),
    source: value.source,
    spawnX,
    spawnY,
    zone: value.zone.trim() || 'default',
    movementConfig,
    waypointPath: toLegacyWaypointPath(movementConfig, waypointPath),
  };

  if (value.source === 'system' || value.source === 'user') {
    const sourceNpcId = (value.sourceNpcId ?? '').trim();
    if (sourceNpcId) {
      normalized.sourceNpcId = sourceNpcId;
    }
  }

  if (value.inlineConfig) {
    const {
      avatarLayers: rawAvatarLayers,
      spriteColors: rawSpriteColors,
      ...inlineConfig
    } = value.inlineConfig;
    const avatarLayers = normalizeAvatarLayerSelection(rawAvatarLayers);
    const spriteColors = normalizeInlineSpriteColors(rawSpriteColors, {
      avatarLayers,
      spriteId: inlineConfig.spriteId,
    });
    normalized.inlineConfig = {
      ...inlineConfig,
      ...(avatarLayers ? { avatarLayers } : {}),
      ...(spriteColors ? { spriteColors } : {}),
      llmModel: canonicalizeProviderModel(inlineConfig.llmProvider, inlineConfig.llmModel),
      fallbackProviders: cloneFallbackProviders(inlineConfig.fallbackProviders),
    };
  }

  return normalized;
};

export const sanitizeSceneNpcPlacements = (
  value: SceneNpcPlacement[],
  layout: WorldLayout,
): SceneNpcPlacement[] =>
  value
    .map((entry, index) => sanitizeSceneNpcPlacement(entry, `placement-${index + 1}`, layout))
    .filter((entry) => {
      if (entry.source === 'inline') {
        return Boolean(entry.inlineConfig?.name?.trim());
      }
      return Boolean(entry.sourceNpcId);
    })
    .slice(0, 20);

export const sanitizeCharacterScale = (
  value: SceneDocument['characterScale'],
): SceneDocument['characterScale'] => {
  const fallback: CharacterScale = DEFAULT_SCENE_CHARACTER_SCALE;
  return normalizeCharacterScale({
    npc: toNumber(value.npc, fallback.npc),
    playerFallback: toNumber(value.playerFallback, fallback.playerFallback),
    playerAvatar: toNumber(value.playerAvatar, fallback.playerAvatar),
    remoteNpc: toNumber(value.remoteNpc, fallback.remoteNpc),
    remoteAvatar: toNumber(value.remoteAvatar, fallback.remoteAvatar),
  });
};

export const sanitizeCharacterScaleMultiplier = (value: unknown): number =>
  normalizeCharacterScaleMultiplier(
    typeof value === 'number' ? value : Number(value ?? DEFAULT_CHARACTER_SCALE_MULTIPLIER),
  );

export const sanitizeAmbientProfile = (
  value: SceneDocument['ambientZoneProfile'],
): SceneDocument['ambientZoneProfile'] => {
  const profile: SceneDocument['ambientZoneProfile'] = {};
  Object.entries(value).forEach(([zoneId, zoneValue]) => {
    const id = zoneId.trim();
    if (!id) {
      return;
    }
    profile[id] = {
      frequency: Math.max(40, Math.min(1200, toNumber(zoneValue.frequency, 120))),
      gain: Math.max(0, Math.min(0.2, toNumber(zoneValue.gain, 0.003))),
    };
  });
  return profile;
};

export const sanitizeWorldAnnotationsForLayout = (
  value: SceneDocument['annotations'],
  layout: WorldLayout,
): SceneDocument['annotations'] =>
  stripRuntimeStateFromAnnotations(normalizeWorldAnnotations(value, layout));

export const sanitizeAnnotationRuntimeStateById = (
  value: SceneDocument['annotationRuntimeStateById'],
): SceneDocument['annotationRuntimeStateById'] => normalizeAnnotationRuntimeStateById(value);

export const sanitizeAnnotationTriggerRuntimeStateById = (
  value: SceneDocument['annotationTriggerRuntimeStateById'],
): SceneDocument['annotationTriggerRuntimeStateById'] => normalizeTriggerRuntimeStateById(value);

export const sanitizeWorldLayout = (
  value: SceneDocument['worldLayout'],
): SceneDocument['worldLayout'] => normalizeWorldLayout(value);

export const sanitizeSceneMetadataForUpdate = (
  current: Record<string, unknown>,
): Pick<SceneDocument, 'name' | 'status' | 'tags'> => {
  const statusFallback = current.publishedAt ? 'published' : 'draft';
  return {
    name: sanitizeSceneName(String(current.name ?? 'Untitled Scene')),
    status: normalizeWritableStatus(normalizeSceneStatus(current.status, statusFallback)),
    tags: sanitizeTags(
      Array.isArray(current.tags)
        ? current.tags
            .map((entry) => String(entry).trim())
            .filter((entry) => entry.length > 0)
            .slice(0, 10)
        : [],
    ),
  };
};

export const assertPublishableScene = (input: SceneCompositionInput): void => {
  if (input.status !== 'published' && input.status !== 'featured') {
    return;
  }
  if (!input.levelDesignId.trim()) {
    throw new Error('A level design is required before publishing.');
  }
  if (!input.backgroundUrl || !input.traversalMaskUrl || !input.thumbnailUrl) {
    throw new Error('Background, traversal mask, and thumbnail are required before publishing.');
  }
  if (input.npcPlacements.length === 0) {
    throw new Error('At least one NPC placement is required before publishing.');
  }
};

export const shouldExternalizeSceneAssetsForSave = (
  input: Pick<SceneCompositionInput, 'levelDesignId' | 'status'>,
): boolean => {
  if (!input.levelDesignId.trim()) {
    return true;
  }

  return input.status === 'published' || input.status === 'featured';
};

export const getDraftShellSceneAssets = (
  input: Pick<
    SceneCompositionInput,
    'animatedGifs' | 'backgroundUrl' | 'foregroundUrl' | 'thumbnailUrl' | 'traversalMaskUrl'
  >,
): Pick<
  SceneDocument,
  'animatedGifs' | 'backgroundUrl' | 'foregroundUrl' | 'thumbnailUrl' | 'traversalMaskUrl'
> => ({
  animatedGifs: sanitizeAnimatedGifPlacements(input.animatedGifs),
  backgroundUrl: input.backgroundUrl?.trim() || null,
  foregroundUrl: input.foregroundUrl?.trim() || null,
  thumbnailUrl: input.thumbnailUrl?.trim() || null,
  traversalMaskUrl: input.traversalMaskUrl?.trim() || null,
});
